import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	ValidationErrors,
	Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { RouteType } from "src/app/models/enum";
import { FeedbackTemplateQuestion } from "src/app/models/models";
import {
	ErrorSnackbarComponent,
	ErrorSnackbarData,
} from "src/app/shared/components/cbta-snackbar/error-snackbar.component";
import { HasUnsavedFormChanges } from "src/app/shared/interfaces/has-unsaved-form-changes";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { openConfirmationDialog } from "src/app/shared/utils/confirmation-dialog.utils";
import { getFormTypeShortLabel } from "src/app/shared/utils/form-type-label";
import { resolveHttpErrorMessageText, toHttpErrorPayload } from "src/app/shared/utils/http-error-message";
import { unwrapFormApiResponse } from "../../forms/ts1-v2/ts1-form.factories";
import { FEEDBACK_RATING_SCALE } from "../feedback-question.utils";
import {
	AttachableFeedbackForm,
	buildFeedbackTemplateRequest,
	buildMoveFormsConfirmation,
	DEPRECATED_STANDALONE_RECIPIENT_ROLE_LABELS,
	mapLinkedFeedbackForms,
	resolveFeedbackTemplateLinks,
	sanitizeStandaloneRecipientRoles,
	utcIsoToLocalDate,
} from "../feedback-template.mapper";
import { FEEDBACK_FORMS_ALREADY_ATTACHED, FEEDBACK_MAX_QUESTIONS, FeedbackTemplateListItem } from "../feedback.types";

function requireNonEmptyArray(control: AbstractControl): ValidationErrors | null {
	const value = control.value;
	return Array.isArray(value) && value.length > 0 ? null : { required: true };
}

function questionHasInput(group: AbstractControl): ValidationErrors | null {
	const rating = group.get("required")?.value !== false;
	const answer = group.get("answerEnabled")?.value !== false;
	return rating || answer ? null : { inputRequired: true };
}

function maxQuestionCount(control: AbstractControl): ValidationErrors | null {
	const length = control instanceof FormArray ? control.length : 0;
	return length > FEEDBACK_MAX_QUESTIONS ? { maxQuestions: { max: FEEDBACK_MAX_QUESTIONS, actual: length } } : null;
}

@Component({
	selector: "app-feedback-form",
	templateUrl: "./feedback-form.component.html",
	styleUrls: ["./feedback-form.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class FeedbackFormComponent implements OnInit, OnDestroy, HasUnsavedFormChanges {
	form!: FormGroup;
	isEdit = false;
	isStandaloneTemplate = false;
	formSuccess = false;
	templateId = "";
	templateReady = false;
	pageTitle = "Create New Feedback Form";
	breadcrumbLabel = "Create New Feedback Form";
	availableForms: AttachableFeedbackForm[] = [];
	loadingForms = false;
	removedRecipientRoleNotice = "";

	readonly maxQuestions = FEEDBACK_MAX_QUESTIONS;
	readonly ratingScale = FEEDBACK_RATING_SCALE;

	readonly recipientRoleOptions = [
		{ value: "pilot", label: "Pilot" },
		{ value: "instructor", label: "Instructor" },
		{ value: "cabin crew", label: "Cabin Crew" },
		{ value: "cabin crew instructor", label: "Cabin Crew Instructor" },
	];

	private subs = new Subscription();
	private isHydratingTemplate = false;
	private formsLoadRequestId = 0;

	constructor(
		private fb: FormBuilder,
		private route: ActivatedRoute,
		private router: Router,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private sharedService: SharedService,
		private snackBar: MatSnackBar,
		private cdr: ChangeDetectorRef,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.FEEDBACK);
		this.buildForm();

		this.subs.add(
			this.route.paramMap.subscribe((params) => {
				const id = params.get("id");
				if (id) {
					this.isEdit = true;
					this.templateId = id;
					this.pageTitle = "Edit Feedback Form";
					this.breadcrumbLabel = "Edit Feedback Form";
					this.templateReady = false;
					this.loadTemplate(id);
					return;
				}

				this.resetForCreate();
			}),
		);

		this.subs.add(
			this.form.get("formTypeCodes")!.valueChanges.subscribe((codes: string[]) => {
				if (this.isHydratingTemplate || this.isStandaloneMode) return;
				const currentFormIds = (this.form.get("formIds")?.value ?? []) as string[];
				this.loadFormsForTypes(codes ?? [], currentFormIds);
			}),
		);

		this.subs.add(
			this.form.get("isStandalone")!.valueChanges.subscribe(() => {
				if (this.isHydratingTemplate || this.isEdit) return;
				this.applyStandaloneValidators();
			}),
		);
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	get filterOptions() {
		return this.sharedService.feedbackAttachableFilterOptions;
	}

	get selectedFormTypeCodes(): string[] {
		return this.form.get("formTypeCodes")?.value ?? [];
	}

	get selectedFormIds(): string[] {
		return this.form.get("formIds")?.value ?? [];
	}

	get selectedRecipientRoles(): string[] {
		return this.form.get("recipientRoles")?.value ?? [];
	}

	get questions(): FormArray {
		return this.form.get("questions") as FormArray;
	}

	get questionGroups(): FormGroup[] {
		return this.questions.controls as FormGroup[];
	}

	get isStandaloneMode(): boolean {
		return this.isEdit ? this.isStandaloneTemplate : !!this.form.get("isStandalone")?.value;
	}

	get typeBadgeLabel(): string {
		return this.isStandaloneMode ? "Standalone" : "Linked to form";
	}

	get startsAtControl(): FormControl<Date | null> {
		return this.form.get("startsAt") as FormControl<Date | null>;
	}

	get endsAtControl(): FormControl<Date | null> {
		return this.form.get("endsAt") as FormControl<Date | null>;
	}

	get scheduleWindowError(): string | null {
		if (!this.isStandaloneMode) return null;
		const start = this.startsAtControl.value;
		const end = this.endsAtControl.value;
		if (!start || !end) return null;
		if (end.getTime() <= start.getTime()) {
			return "End time must be after start time.";
		}
		return null;
	}

	buildForm(): void {
		this.form = this.fb.group({
			name: ["", [Validators.required, Validators.maxLength(200)]],
			isStandalone: [false],
			recipientRoles: [[]],
			startsAt: [null as Date | null],
			endsAt: [null as Date | null],
			formTypeCodes: [[], requireNonEmptyArray],
			formIds: [[]],
			questions: this.fb.array([this.createQuestionGroup()], maxQuestionCount),
		});
		this.applyStandaloneValidators();
	}

	createQuestionGroup(question?: Partial<FeedbackTemplateQuestion>): FormGroup {
		const answerEnabled = question?.answerEnabled !== false;
		return this.fb.group(
			{
				id: [question?.id ?? ""],
				prompt: [question?.prompt ?? "", [Validators.required, Validators.maxLength(1000)]],
				required: [question?.required !== false],
				answerEnabled: [answerEnabled],
				answerRequired: [answerEnabled && question?.answerRequired === true],
			},
			{ validators: [questionHasInput] },
		);
	}

	get questionLimitReached(): boolean {
		return this.questions.length >= this.maxQuestions;
	}

	addQuestion(): void {
		if (this.questionLimitReached) return;
		this.questions.push(this.createQuestionGroup());
	}

	removeQuestion(question: FormGroup): void {
		if (this.questions.length <= 1) return;
		const index = this.questions.controls.indexOf(question);
		if (index < 0) return;
		this.questions.removeAt(index);
	}

	formTypeLabel(code: string): string {
		return getFormTypeShortLabel(code);
	}

	recipientRoleLabel(code: string): string {
		return (
			this.recipientRoleOptions.find((option) => option.value === code)?.label ??
			DEPRECATED_STANDALONE_RECIPIENT_ROLE_LABELS[code] ??
			code
		);
	}

	removeFormTypeCode(code: string): void {
		const updated = this.selectedFormTypeCodes.filter((item) => item !== code);
		this.form.get("formTypeCodes")!.setValue(updated);
	}

	removeFormId(id: string): void {
		const updated = this.selectedFormIds.filter((item) => item !== id);
		this.form.get("formIds")!.setValue(updated);
	}

	removeRecipientRole(code: string): void {
		const updated = this.selectedRecipientRoles.filter((item) => item !== code);
		this.form.get("recipientRoles")!.setValue(updated);
	}

	loadFormsForTypes(formTypeCodes: string[], preserveFormIds: string[] = []): void {
		const requestId = ++this.formsLoadRequestId;
		const selectedCodes = new Set(formTypeCodes);

		this.availableForms = this.availableForms.filter(
			(form) => !!form.formType?.code && selectedCodes.has(form.formType.code),
		);
		const remainingIds = preserveFormIds.filter((id) => this.availableForms.some((form) => form.id === id));
		this.form.get("formIds")!.setValue(remainingIds, { emitEvent: false });

		if (!formTypeCodes.length) {
			this.loadingForms = false;
			this.cdr.detectChanges();
			return;
		}

		this.loadingForms = true;
		this.subs.add(
			this.httpService.forms({ page: 1, size: 200, formTypes: formTypeCodes, feedbackAttachable: true }).subscribe({
				next: (resp) => {
					if (requestId !== this.formsLoadRequestId) return;
					const fetchedForms = this.unwrapFormsList(resp).filter(
						(form) => !!form.formType?.code && selectedCodes.has(form.formType.code),
					);
					this.availableForms = this.mergeAttachableForms(this.availableForms, fetchedForms);

					const validIds = preserveFormIds.filter((id) => this.availableForms.some((form) => form.id === id));
					this.form.get("formIds")!.setValue(validIds, { emitEvent: false });

					this.loadingForms = false;
					this.cdr.detectChanges();
				},
				error: () => {
					if (requestId !== this.formsLoadRequestId) return;
					this.loadingForms = false;
					this.cdr.detectChanges();
				},
			}),
		);
	}

	formLabel(formId: string): string {
		const form = this.availableForms.find((item) => item.id === formId);
		if (!form) return formId;
		if (this.selectedFormTypeCodes.length > 1 && form.formType?.code) {
			return `${form.name} — ${this.formTypeLabel(form.formType.code)}`;
		}
		return form.name;
	}

	loadTemplate(id: string): void {
		this.formsLoadRequestId += 1;
		this.formSuccess = false;
		this.removedRecipientRoleNotice = "";
		this.loaderService.setLoading(true);
		this.isHydratingTemplate = true;

		this.subs.add(
			this.httpService.getFeedbackTemplate(id).subscribe({
				next: (resp) => {
					const data = unwrapFormApiResponse<FeedbackTemplateListItem>(resp);
					const links = resolveFeedbackTemplateLinks(data);
					this.isStandaloneTemplate = links.isStandalone;
					const { formTypeCodes, formIds } = links;
					const recipientRoleState = links.isStandalone
						? sanitizeStandaloneRecipientRoles(data.recipientRoles)
						: { roles: data.recipientRoles ?? [], removedRoles: [] as string[] };

					this.removedRecipientRoleNotice = recipientRoleState.removedRoles.length
						? `The following recipient roles are no longer supported and were removed: ${recipientRoleState.removedRoles.map((role) => this.recipientRoleLabel(role)).join(", ")}. Review recipients before saving.`
						: "";

					this.availableForms = mapLinkedFeedbackForms(data.linkedForms);

					this.questions.clear();
					(data.questions ?? []).forEach((q) => {
						this.questions.push(this.createQuestionGroup(q));
					});
					if (this.questions.length === 0) {
						this.questions.push(this.createQuestionGroup());
					}

					this.form.patchValue(
						{
							name: data.name ?? "",
							isStandalone: this.isStandaloneTemplate,
							recipientRoles: recipientRoleState.roles,
							startsAt: utcIsoToLocalDate(data.startsAt),
							endsAt: utcIsoToLocalDate(data.endsAt),
							formTypeCodes,
							formIds,
						},
						{ emitEvent: false },
					);

					if (!this.isStandaloneTemplate && formTypeCodes.length) {
						this.loadFormsForTypes(formTypeCodes, formIds);
					}

					this.applyStandaloneValidators();
					if (recipientRoleState.removedRoles.length) {
						this.form.markAsDirty();
					} else {
						this.form.markAsPristine();
					}
					this.isHydratingTemplate = false;
					this.templateReady = true;
					this.loaderService.setLoading(false);
					this.cdr.detectChanges();
				},
				error: () => {
					this.formSuccess = false;
					this.isHydratingTemplate = false;
					this.loaderService.setLoading(false);
					this.showError("Unable to load feedback template");
					this.router.navigate([RouteType.FEEDBACK]);
				},
			}),
		);
	}

	hasUnsavedFormChanges(): boolean {
		return !this.formSuccess && this.form?.dirty === true;
	}

	cancel(): void {
		void this.router.navigate([RouteType.FEEDBACK]);
	}

	async submit(replaceExistingAttachments = false): Promise<void> {
		this.applyStandaloneValidators();
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		if (this.isStandaloneMode && this.scheduleWindowError) {
			this.showError(this.scheduleWindowError);
			this.endsAtControl.markAsTouched();
			return;
		}

		if (!replaceExistingAttachments && !this.isStandaloneMode) {
			const conflicts = this.conflictingAttachedForms();
			if (conflicts.length) {
				const confirmed = await this.confirmMoveAttachedForms(conflicts.map((form) => form.name));
				if (!confirmed) return;
				replaceExistingAttachments = true;
			}
		}

		const payload = buildFeedbackTemplateRequest({
			isEdit: this.isEdit,
			standalone: this.isStandaloneMode,
			name: String(this.form.value.name ?? ""),
			recipientRoles: this.form.value.recipientRoles ?? [],
			startsAt: this.form.value.startsAt ?? null,
			endsAt: this.form.value.endsAt ?? null,
			formIds: this.form.value.formIds ?? [],
			formTypeCodes: this.form.value.formTypeCodes ?? [],
			replaceExistingAttachments,
			questions: this.questions.controls.map((ctrl) => ({
				id: ctrl.get("id")?.value,
				prompt: ctrl.get("prompt")?.value,
				required: ctrl.get("required")?.value,
				answerEnabled: ctrl.get("answerEnabled")?.value,
				answerRequired: ctrl.get("answerRequired")?.value,
			})),
		});

		this.loaderService.setLoading(true);
		const request$ = this.isEdit
			? this.httpService.updateFeedbackTemplate(this.templateId, payload)
			: this.httpService.createFeedbackTemplate(payload);

		this.subs.add(
			request$.subscribe({
				next: () => {
					this.formSuccess = true;
					this.loaderService.setLoading(false);
					this.router.navigate([RouteType.FEEDBACK]);
				},
				error: async (err) => {
					this.formSuccess = false;
					this.loaderService.setLoading(false);
					const payloadError = toHttpErrorPayload(err);
					if (
						!replaceExistingAttachments &&
						payloadError.code === FEEDBACK_FORMS_ALREADY_ATTACHED &&
						(payloadError.formNames?.length ?? 0) > 0
					) {
						const confirmed = await this.confirmMoveAttachedForms(payloadError.formNames ?? []);
						if (confirmed) {
							void this.submit(true);
						}
						return;
					}

					const message = resolveHttpErrorMessageText(err);
					if (payloadError.status === 409 && message.includes("title already exists")) {
						this.form.get("name")?.setErrors({ duplicate: message });
						this.form.get("name")?.markAsTouched();
					}
					this.showError(message);
				},
			}),
		);
	}

	private applyStandaloneValidators(): void {
		const standalone = this.isStandaloneMode;
		const recipientControl = this.form.get("recipientRoles");
		const formTypeControl = this.form.get("formTypeCodes");
		const formIdsControl = this.form.get("formIds");

		if (standalone) {
			recipientControl?.setValidators(requireNonEmptyArray);
			formTypeControl?.clearValidators();
			formIdsControl?.clearValidators();
			formTypeControl?.setValue([], { emitEvent: false });
			formIdsControl?.setValue([], { emitEvent: false });
		} else {
			recipientControl?.clearValidators();
			recipientControl?.setValue([], { emitEvent: false });
			formTypeControl?.setValidators(requireNonEmptyArray);
			formIdsControl?.clearValidators();
		}

		recipientControl?.updateValueAndValidity({ emitEvent: false });
		formTypeControl?.updateValueAndValidity({ emitEvent: false });
		formIdsControl?.updateValueAndValidity({ emitEvent: false });
	}

	private conflictingAttachedForms(): AttachableFeedbackForm[] {
		const selected = new Set(this.selectedFormIds);
		return this.availableForms.filter((form) => {
			if (!selected.has(form.id)) return false;
			const attachedTemplateId = form.feedbackTemplateId?.trim();
			if (!attachedTemplateId) return false;
			return attachedTemplateId !== this.templateId;
		});
	}

	private confirmMoveAttachedForms(formNames: string[]): Promise<boolean> {
		return openConfirmationDialog(this.dialog, {
			title: "CAUTION: Replace Feedback Form?",
			content: buildMoveFormsConfirmation(formNames),
			confirmText: "Replace",
			cancelText: "Cancel",
			width: "520px",
			height: "260px",
		});
	}

	private resetForCreate(): void {
		this.formsLoadRequestId += 1;
		this.isEdit = false;
		this.isStandaloneTemplate = false;
		this.formSuccess = false;
		this.templateId = "";
		this.pageTitle = "Create New Feedback Form";
		this.breadcrumbLabel = "Create New Feedback Form";
		this.availableForms = [];
		this.removedRecipientRoleNotice = "";
		this.isHydratingTemplate = false;
		this.templateReady = true;

		this.questions.clear();
		this.questions.push(this.createQuestionGroup());
		this.form.patchValue({
			name: "",
			isStandalone: false,
			recipientRoles: [],
			startsAt: null,
			endsAt: null,
			formTypeCodes: [],
			formIds: [],
		});
		this.applyStandaloneValidators();
		this.form.markAsPristine();
		this.form.markAsUntouched();
		this.cdr.detectChanges();
	}

	private unwrapFormsList(resp: unknown): AttachableFeedbackForm[] {
		const payload = unwrapFormApiResponse<{ data?: AttachableFeedbackForm[] } | AttachableFeedbackForm[]>(
			resp as { data?: AttachableFeedbackForm[] } | AttachableFeedbackForm[],
		);
		if (Array.isArray(payload)) {
			return payload;
		}
		return Array.isArray(payload?.data) ? payload.data : [];
	}

	private mergeAttachableForms(
		existing: AttachableFeedbackForm[],
		incoming: AttachableFeedbackForm[],
	): AttachableFeedbackForm[] {
		const merged = new Map<string, AttachableFeedbackForm>();
		[...existing, ...incoming].forEach((form) => {
			if (form?.id) merged.set(form.id, form);
		});
		return Array.from(merged.values());
	}

	private showError(message: string): void {
		this.snackBar.openFromComponent(ErrorSnackbarComponent, {
			data: { message } as ErrorSnackbarData,
			duration: 5000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["error-snackbar"],
		});
	}
}
